import { FolderUpdateApi } from '../models/api'
import { Observable, map } from 'rxjs'

import { Deliverable } from '@app/features/scoping/models/deliverable.model'
import { FolderVersion } from '@app/core/model/folder-version.model'
import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { ScopeVersion } from '@app/core/model/scope-version'
import { SowRetainedHoursStats } from '@app/features/scope-overview/model/sow-retained-hours-stats.model'
import { environment } from '@envs/environment'
import { plainToInstance } from 'class-transformer'
import { ScopeComponent } from '@app/features/scoping/models/component.model'
import { DeliverableSection } from '@app/features/scope-overview/model/deliverable-section'

@Injectable({ providedIn: 'root' })
export class FolderOverviewApiService {
  private readonly BASE_API_URL: string = environment.scopeApiUrl

  constructor(private http: HttpClient) {}

  getFolder(id: number): Observable<FolderVersion> {
    return this.http
      .get<FolderVersion>(`${this.BASE_API_URL}sow/${id}`)
      .pipe(map((api) => plainToInstance(FolderVersion, api)))
  }

  getFolderVersionById(id: number, version: number): Observable<FolderVersion> {
    return this.http
      .get<FolderVersion>(`${this.BASE_API_URL}sow/${id}/version/${version}`)
      .pipe(map((api) => plainToInstance(FolderVersion, api)))
  }

  updateFolder(id: number, payload: FolderUpdateApi): Observable<FolderVersion> {
    return this.http
      .put<FolderVersion>(`${this.BASE_API_URL}sow/${id}`, payload)
      .pipe(map((api) => plainToInstance(FolderVersion, api)))
  }

  deleteFolder(id: number): Observable<any> {
    return this.http.delete<any>(`${this.BASE_API_URL}sow/${id}`, {})
  }

  archiveFolder(id: number): Observable<any> {
    return this.http.put<any>(`${this.BASE_API_URL}sow/${id}/archive`, {})
  }

  getRetainedHoursStats(id: number): Observable<SowRetainedHoursStats> {
    return this.http.get<SowRetainedHoursStats>(`${this.BASE_API_URL}sow/${id}/retained-hours-stats`)
  }

  deleteScope(scopeId: number): Observable<any> {
    return this.http.delete<any>(`${this.BASE_API_URL}scope/${scopeId}`, {})
  }

  removeScope(scopeId: number): Observable<any> {
    return this.http.post<any>(`${this.BASE_API_URL}scope/${scopeId}/un-assign-from-sow`, {})
  }

  updateScope(scopeId: number, scope: ScopeVersion): Observable<ScopeVersion> {
    return this.http
      .put<ScopeVersion>(`${this.BASE_API_URL}scope/${scopeId}`, scope)
      .pipe(map((api) => plainToInstance(ScopeVersion, api)))
  }

  updateDeliverable(scopeId: number, deliverable: Deliverable) {
    return this.http
      .put(`${this.BASE_API_URL}scope/${scopeId}/deliverable/${deliverable.id}`, deliverable)
      .pipe(map((api) => plainToInstance(Deliverable, api)))
  }

  updateComponent(scopeId: number, component: ScopeComponent) {
    return this.http
      .put(
        `${this.BASE_API_URL}scope/${scopeId}/deliverable/${component.deliverable.id}/component/${component.id}`,
        component
      )
      .pipe(map((api) => plainToInstance(ScopeComponent, api)))
  }

  updateDeliverableSection(scopeId: number, section: DeliverableSection) {
    return this.http
      .put(`${this.BASE_API_URL}scope/${scopeId}/deliverable/${section.deliverableId}/section/${section.id}`, section)
      .pipe(map((api) => plainToInstance(DeliverableSection, api)))
  }

  getScopeById(scopeId: number, version?: number): Observable<ScopeVersion> {
    return this.http
      .get<ScopeVersion>(`${this.BASE_API_URL}scope/${scopeId}` + (version ? `?version=${version}` : ''))
      .pipe(map((api) => plainToInstance(ScopeVersion, api)))
  }

  getDeliverableById(scopeId: number, deliverableId: number): Observable<Deliverable> {
    return this.http
      .get<Deliverable>(`${this.BASE_API_URL}scope/${scopeId}/deliverable/${deliverableId}`)
      .pipe(map((api) => plainToInstance(Deliverable, api)))
  }

  trafficScopes(id: number, scopeIds: number[]): Observable<any> {
    return this.http
      .get<any>(`${this.BASE_API_URL}sow/${id}/traffic?scopeIds=${scopeIds.join(',')}`)
      .pipe(map((api) => api.statusList))
  }
}
